import config from './config';

/**
 * Password policy configuration per site.
 *
 * Password policies define validation rules for user passwords during registration,
 * password change, and password reset flows.
 *
 * ---.
 *
 * ## Keeping Password Policy in Sync with Auth Adapter
 *
 * Password policy requirements are determined by the authentication backend.
 * When changing the `IAuthServiceAdapter` in the DI container, you **must** also
 * update the password policy here to match.
 *
 * | Auth Adapter              | Password Policy             | Notes                          |
 * |---------------------------|-----------------------------|--------------------------------|
 * | `SLASAuthServiceAdapter`  | Default (strict)            | Requires numbers + special chars |
 * | `CognitoAuthServiceAdapter` | Override (less strict)    | No numbers/special chars required |
 *
 * @see libs/services/src/dependency-injection/container/index.ts - Auth adapter bindings
 * @see libs/services/src/isomorphic/PasswordPolicyService - Consumes this config
 *
 * ---
 */
const passwordPolicy = config({
  /** Defaults for all sites and locales. */
  default: {
    /** Default settings for all languages. */
    default: {
      /**
       * SLAS/SFCC Password Policy (default for most sites).
       * SLAS requires stricter passwords including numbers and special characters.
       *
       * @see https://sfcclearning.com/infocenter/LegacyDevDoc/SiteGenesisPasswords.php
       */

      /** Minimum password length. */
      minLength: 8,

      /** Maximum password length. */
      maxLength: 255,

      /** Require at least one uppercase letter. */
      requireUppercase: true,

      /** Require at least one lowercase letter. */
      requireLowercase: true,

      /** Require at least one numeric digit. */
      requireNumber: true,

      /** Require at least one special character. */
      requireSpecialChar: true
    }
  },

  /**
   * AHNU-US uses CognitoAuthServiceAdapter.
   * Cognito has less strict default requirements.
   *
   * If you change AHNU-US to use a different auth adapter in container/index.ts,
   * update this policy accordingly.
   */
  'AHNU-US': { default: { requireNumber: false, requireSpecialChar: false } }
});

export default passwordPolicy;
